import React, { useState } from "react";
import { fr } from "date-fns/locale";
import { CalendarDays, Clock } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const pad = (n) => String(n).padStart(2, "0");
export const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fromYmd = (v) => (v && /^\d{4}-\d{2}-\d{2}$/.test(v) ? new Date(`${v}T12:00:00`) : undefined);
export const todayYmd = () => toYmd(new Date());

export const fmtDayFr = (v) => { const d = fromYmd(v); return d ? d.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" }) : ""; };
export const fmtParisLong = (iso) => (iso ? new Date(iso).toLocaleString("fr-FR", { timeZone: "Europe/Paris", weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }).replace(":", "h") : "—");

const timesFor = (step) => Array.from({ length: (24 * 60) / step }, (_, i) => { const m = i * step; return `${pad(Math.floor(m / 60))}:${pad(m % 60)}`; });
const TIMES = { 15: timesFor(15), 60: timesFor(60) };

const DAY_CLASSES = {
  head_cell: "text-white/45 w-10 font-normal text-[0.75rem] uppercase",
  cell: "relative p-0 text-center text-sm",
  day: "h-10 w-10 p-0 font-normal text-white/85 hover:bg-[#DCB457]/15 hover:text-[#DCB457] transition-colors",
  day_selected: "!bg-[#DCB457] !text-black hover:!bg-[#DCB457]",
  day_today: "border border-[#DCB457]/50 text-[#DCB457]",
  day_outside: "text-white/20",
  day_disabled: "text-white/15 line-through pointer-events-none",
  caption_label: "text-sm font-serif-luxe capitalize text-white",
  nav_button: "h-8 w-8 inline-flex items-center justify-center border border-white/15 text-white/70 hover:text-[#DCB457] hover:border-[#DCB457]/60",
};

export const fmtDmy = (v) => { const d = fromYmd(String(v || "").slice(0, 10)); return d ? `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}` : ""; };
const weekday = (v) => fromYmd(v)?.toLocaleDateString("fr-FR", { weekday: "long" }) || "";

export function DatePickerField({ value, onChange, min, placeholder = "JJ/MM/AAAA", testid, invalid, clearable, className = "" }) {
  const [open, setOpen] = useState(false);
  const minD = fromYmd(min);
  const v = String(value || "").slice(0, 10);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className={`relative ${className}`}>
        <PopoverTrigger asChild>
          <button type="button" data-testid={testid} aria-invalid={invalid || undefined}
            className={`input-luxe w-full min-h-[48px] flex items-center gap-3 text-left ${invalid ? "!border-red-400/70" : ""}`}>
            <CalendarDays size={16} className="text-[#DCB457] shrink-0" />
            <span className={`truncate ${v ? "text-white" : "text-white/40"}`} data-testid={testid ? `${testid}-value` : undefined}>{fromYmd(v) ? <>{fmtDmy(v)} <span className="text-white/45 text-xs">{weekday(v)}</span></> : placeholder}</span>
          </button>
        </PopoverTrigger>
        {clearable && v && <button type="button" onClick={() => onChange("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-red-300 text-xs" aria-label="Effacer" data-testid={testid ? `${testid}-clear` : undefined}>✕</button>}
      </div>
      <PopoverContent align="start" className="z-[200] w-auto p-2 bg-[#0b0b0b] border border-[#DCB457]/35 rounded-none text-white" data-testid={testid ? `${testid}-calendar` : undefined}>
        <Calendar mode="single" locale={fr} weekStartsOn={1} selected={fromYmd(v)} defaultMonth={fromYmd(v) || minD}
          disabled={minD ? { before: minD } : undefined} classNames={DAY_CLASSES}
          onSelect={(d) => { if (d) { onChange(toYmd(d)); setOpen(false); } }} />
      </PopoverContent>
    </Popover>
  );
}

export function TimeSelectField({ value, onChange, testid, placeholder = "Heure", invalid, step = 15 }) {
  const base = TIMES[step] || TIMES[15];
  const opts = value && !base.includes(value) ? [value, ...base] : base;
  return (
    <div className={`flex items-stretch border min-h-[48px] bg-black/60 ${invalid ? "border-red-400/70" : "border-white/15"}`}>
      <span className="flex items-center justify-center w-11 shrink-0 border-r border-white/10"><Clock size={16} className="text-[#DCB457]" /></span>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="flex-1 min-w-0 bg-transparent px-3 text-white outline-none appearance-auto">
        <option value="" className="bg-black">{placeholder}</option>
        {opts.map((t) => <option key={t} value={t} className="bg-black">{t.replace(":", "h")}</option>)}
      </select>
    </div>
  );
}

export function DateTimeField({ date, time, onChange, min, testid, invalid }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-2">
      <DatePickerField value={date} min={min} onChange={(d) => onChange(d, time)} testid={`${testid}-date`} invalid={invalid} />
      <TimeSelectField value={time} onChange={(t) => onChange(date, t)} testid={`${testid}-time`} invalid={invalid} />
    </div>
  );
}

export const isAfter = (d1, t1, d2, t2) => `${d2}T${t2 || "23:59"}` > `${d1}T${t1 || "00:00"}`;

export const parisParts = (iso) => {
  if (!iso) return ["", ""];
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Paris", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(new Date(iso)).map((x) => [x.type, x.value]));
  return [`${p.year}-${p.month}-${p.day}`, `${p.hour}:${p.minute}`];
};

export function RentalPeriodNote({ rental, publicView }) {
  const mode = rental.mode === "livraison" ? "livraison" : "retrait";
  return (
    <div className="border border-[#DCB457]/25 bg-[#DCB457]/5 p-4 text-sm space-y-1 mt-3" data-testid="rental-period-note">
      <p><span className="text-white/50">Départ du matériel ({mode}) : </span><b className="text-white" data-testid="rental-period-start">{fmtParisLong(rental.start)}</b></p>
      <p><span className="text-white/50">Retour du matériel (restitution) : </span><b className="text-white" data-testid="rental-period-end">{fmtParisLong(rental.end)}</b></p>
      {!publicView && <p className="text-[11px] text-white/45">Dossier {rental.number} — dates modifiables depuis le dossier de location.</p>}
    </div>
  );
}
